import React, {useContext, useMemo} from 'react'
import {useSelector} from 'react-redux'
import {HotelCardContext} from 'components/HotelCardContext'
import {HotelCardDesktop} from 'components/HotelCardDesktop'
import {HotelCardMobile} from 'components/HotelCardMobile'
import {useDatelessSeoSRP} from 'hooks/useDatelessSeoSRP'
import {
  getAnchorComplete,
  getHotel,
  getHotelHasOffers,
  getHotelsHaveStaticPosition,
  getIsSearchCompleted
} from 'modules/sapiSearch/selectors'
import {
  getIsAnchorHotelHidden,
  getIsUserInteractedWithFilters
} from 'modules/search/selectors'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'

import {HotelCardLoading} from './HotelCardLoading'

/**
 * Component responsible for rendering either the desktop or mobile hotel card
 */
interface Props {
  /** Whether to show the skeleton loader */
  isLoading?: boolean
  /** Whether to show the divider between the cards */
  shouldShowDivider?: boolean
  /** Whether the card is a priority card (card in the first position of the list) */
  isPriorityCard?: boolean
}

export const HotelCard: React.FC<Props> = ({
  isLoading,
  shouldShowDivider = true,
  isPriorityCard = false
}) => {
  const isDatelessSeoSRP = useDatelessSeoSRP()
  const hotelsHaveStaticPosition = useSelector(getHotelsHaveStaticPosition)
  const {isAnchor, hotelId} = useContext(HotelCardContext)
  const hasOffers = useSelector(state => getHotelHasOffers(state, hotelId))
  const isAnchorCompleted = useSelector(getAnchorComplete)
  const isSearchCompleted = useSelector(getIsSearchCompleted)
  const hotel = useSelector(state => getHotel(state, hotelId))
  const isAnchorHotelHidden = useSelector(getIsAnchorHotelHidden)
  const isUserInteractedWithFilters = useSelector(
    getIsUserInteractedWithFilters
  )

  const shouldHideAnchorHotel = useMemo(
    () => isAnchorCompleted && !hotel && isAnchor,
    [isAnchorCompleted, hotel, isAnchor]
  )

  const showLoading = useMemo(() => {
    if (isLoading || !hotelId) return true
    if (
      !hasOffers &&
      !isAnchor &&
      !hotelsHaveStaticPosition &&
      !isSearchCompleted
    )
      return true

    return false
  }, [
    hotelsHaveStaticPosition,
    isAnchor,
    isLoading,
    hasOffers,
    hotelId,
    isSearchCompleted
  ])

  const {isMobile, isDesktopXs} = useDeviceLayout()

  if (
    (isAnchor && isAnchorHotelHidden && hotel) ||
    (isAnchor &&
      !isSearchCompleted &&
      isUserInteractedWithFilters &&
      !hasOffers)
  )
    return null

  if (showLoading && !isDatelessSeoSRP) return <HotelCardLoading />

  if (shouldHideAnchorHotel) return null

  if (isMobile || isDesktopXs)
    return <HotelCardMobile isPriorityCard={isPriorityCard} />

  // Showing Hotel Card Desktop view
  return (
    <HotelCardDesktop
      shouldShowDivider={shouldShowDivider}
      isPriorityCard={isPriorityCard}
    />
  )
}
